// client/src/components/PublicProfile/PublicStatistics.js
import React from 'react';
import { 
  Trophy, Target, TrendingUp, Award, Users, Zap, 
  DollarSign, Star, Activity, Calendar, Crown 
} from 'lucide-react';

const PublicStatistics = ({ profileData, compact = false, t }) => {
  // Performance level calculation
  const getPerformanceLevel = (winRate) => {
    const rate = parseFloat(winRate);
    if (rate >= 80) return { level: 'elite', text: t('elite', 'Elite'), color: '#fbbf24', icon: Crown };
    if (rate >= 70) return { level: 'pro', text: t('pro', 'Pro'), color: '#8b5cf6', icon: Trophy };
    if (rate >= 60) return { level: 'advanced', text: t('advanced', 'Advanced'), color: '#3b82f6', icon: TrendingUp };
    if (rate >= 40) return { level: 'average', text: t('average', 'Average'), color: '#10b981', icon: Target };
    return { level: 'beginner', text: t('beginner', 'Beginner'), color: '#6b7280', icon: Users };
  };

  const performance = getPerformanceLevel(profileData.winRate);
  const PerformanceIcon = performance.icon;

  // Format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  // Calculate additional stats
  const totalPointsEarned = (profileData.pointsWon || 0) + (profileData.pointsPurchased || 0);
  const netPoints = (profileData.pointsWon || 0) - (profileData.pointsLost || 0);
  const averagePointsPerWin = profileData.wins > 0 ? Math.round((profileData.pointsWon || 0) / profileData.wins) : 0;

  if (compact) {
    return (
      <div className="public-statistics compact">
        <div className="statistics-header">
          <h3 className="statistics-title">
            <TrendingUp className="w-4 h-4" />
            {t('quickStats', 'Quick Stats')}
          </h3>
          <div className="performance-badge compact">
            <PerformanceIcon className="w-3 h-3" style={{ color: performance.color }} />
            <span style={{ color: performance.color }}>{performance.text}</span>
          </div>
        </div>

        <div className="stats-grid compact">
          <div className="stat-card">
            <div className="stat-icon">
              <Trophy className="w-4 h-4" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{profileData.wins}</div>
              <div className="stat-label">{t('wins', 'Wins')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Activity className="w-4 h-4" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{profileData.totalGames}</div>
              <div className="stat-label">{t('matches', 'Matches')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{profileData.winRate}%</div>
              <div className="stat-label">{t('winRate', 'Win Rate')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Zap className="w-4 h-4" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{profileData.currentWinStreak}</div>
              <div className="stat-label">{t('streak', 'Streak')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-statistics">
      {/* Header with Performance Badge */}
      <div className="statistics-header">
        <h3 className="statistics-title">
          <TrendingUp className="w-5 h-5" />
          {t('detailedStatistics', 'Detailed Statistics')}
        </h3>
        <div className="performance-badge" style={{ borderColor: performance.color }}>
          <PerformanceIcon className="w-4 h-4" style={{ color: performance.color }} />
          <span style={{ color: performance.color }}>{performance.text} {t('player', 'Player')}</span>
        </div>
      </div>

      {/* Main Statistics Grid */}
      <div className="main-stats-section">
        <h4 className="section-title">{t('overallPerformance', 'Overall Performance')}</h4>
        <div className="stats-grid">
          <div className="stat-card highlight">
            <div className="stat-header">
              <Trophy className="w-5 h-5" />
              <span>{t('totalWins', 'Total Wins')}</span>
            </div>
            <div className="stat-value large">{profileData.wins}</div>
            <div className="stat-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill wins"
                  style={{ width: `${Math.min(100, (profileData.wins / Math.max(profileData.totalGames, 1)) * 100)}%` }}
                ></div>
              </div>
              <span className="progress-text">{profileData.winRate}% {t('winRate', 'Win Rate')}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <Activity className="w-5 h-5" />
              <span>{t('totalMatches', 'Total Matches')}</span>
            </div>
            <div className="stat-value">{profileData.totalGames}</div>
            <div className="stat-meta">
              {profileData.wins} {t('wins', 'Wins')} • {profileData.losses} {t('losses', 'Losses')}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <Zap className="w-5 h-5" />
              <span>{t('winStreak', 'Win Streak')}</span>
            </div>
            <div className="stat-value">{profileData.currentWinStreak}</div>
            <div className="stat-meta">
              {t('best', 'Best')}: {profileData.maxWinStreak}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <Target className="w-5 h-5" />
              <span>{t('globalRank', 'Global Rank')}</span>
            </div>
            <div className="stat-value">#{profileData.rank}</div>
            <div className="stat-meta">
              {t('byExperience', 'By Experience')}
            </div>
          </div>
        </div>
      </div>

      {/* Game Mode Statistics */}
      <div className="game-modes-section">
        <h4 className="section-title">{t('gameModeBreakdown', 'Game Mode Breakdown')}</h4>
        <div className="game-modes-grid">
          <div className="game-mode-card multiplayer">
            <div className="game-mode-header">
              <Target className="w-5 h-5" />
              <div className="game-mode-info">
                <h5>{t('multiplayer', 'Multiplayer')}</h5>
                <span className="game-mode-desc">{t('teamBased', 'Team-based matches')}</span>
              </div>
            </div>
            <div className="game-mode-stats">
              <div className="mode-stat-row">
                <span className="stat-label">{t('matchesPlayed', 'Matches Played')}</span>
                <span className="stat-value">{profileData.multiplayerPlayed}</span>
              </div>
              <div className="mode-stat-row">
                <span className="stat-label">{t('wins', 'Wins')}</span>
                <span className="stat-value">{profileData.multiplayerWins}</span>
              </div>
              <div className="mode-stat-row highlight">
                <span className="stat-label">{t('winRate', 'Win Rate')}</span>
                <span className="stat-value">{profileData.multiplayerWinRate}%</span>
              </div>
            </div>
            <div className="mode-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill multiplayer"
                  style={{ width: `${Math.min(100, profileData.multiplayerWinRate || 0)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="game-mode-card battle-royale">
            <div className="game-mode-header">
              <Users className="w-5 h-5" />
              <div className="game-mode-info">
                <h5>Battle Royale</h5>
                <span className="game-mode-desc">{t('survivalMode', 'Survival mode')}</span>
              </div>
            </div>
            <div className="game-mode-stats">
              <div className="mode-stat-row">
                <span className="stat-label">{t('matchesPlayed', 'Matches Played')}</span>
                <span className="stat-value">{profileData.battleRoyalePlayed}</span>
              </div>
              <div className="mode-stat-row">
                <span className="stat-label">{t('wins', 'Wins')}</span>
                <span className="stat-value">{profileData.battleRoyaleWins}</span>
              </div>
              <div className="mode-stat-row highlight">
                <span className="stat-label">{t('winRate', 'Win Rate')}</span>
                <span className="stat-value">{profileData.battleRoyaleWinRate}%</span>
              </div>
            </div>
            <div className="mode-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill battle-royale"
                  style={{ width: `${Math.min(100, profileData.battleRoyaleWinRate || 0)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Points Statistics */}
      <div className="points-section">
        <h4 className="section-title">{t('pointsStatistics', 'Points Statistics')}</h4>
        <div className="points-grid">
          <div className="stat-card points">
            <div className="stat-header">
              <DollarSign className="w-5 h-5" />
              <span>{t('currentPoints', 'Current Points')}</span>
            </div>
            <div className="stat-value large">{formatNumber(profileData.points)}</div>
            <div className="stat-meta">
              {t('netGain', 'Net Gain')}: {formatNumber(netPoints)}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <TrendingUp className="w-5 h-5" />
              <span>{t('pointsWon', 'Points Won')}</span>
            </div>
            <div className="stat-value">{formatNumber(profileData.pointsWon)}</div>
            <div className="stat-meta">
              {t('avgPerWin', 'Avg per win')}: {formatNumber(averagePointsPerWin)}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <Target className="w-5 h-5" />
              <span>{t('pointsLost', 'Points Lost')}</span>
            </div>
            <div className="stat-value">{formatNumber(profileData.pointsLost)}</div>
            <div className="stat-meta">
              {t('fromLosses', 'From losses')}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <Star className="w-5 h-5" />
              <span>{t('pointsPurchased', 'Points Purchased')}</span>
            </div>
            <div className="stat-value">{formatNumber(profileData.pointsPurchased)}</div>
            <div className="stat-meta">
              {t('totalEarned', 'Total earned')}: {formatNumber(totalPointsEarned)}
            </div>
          </div>
        </div>
      </div>

      {/* Special Achievements */}
      {(profileData.fillerWins > 0 || profileData.maxWinStreak >= 5) && (
        <div className="achievements-section">
          <h4 className="section-title">{t('specialAchievements', 'Special Achievements')}</h4>
          <div className="achievements-grid">
            {profileData.fillerWins > 0 && (
              <div className="achievement-card">
                <div className="achievement-icon">
                  <Star className="w-6 h-6" />
                </div>
                <div className="achievement-content">
                  <h5>{t('fillerMaster', 'Filler Master')}</h5>
                  <p>{profileData.fillerWins} {t('fillerWins', 'filler wins')}</p>
                  <div className="achievement-desc">
                    {t('fillerWinsDesc', 'Won tournaments with AI fillers')}
                  </div>
                </div>
              </div>
            )}
            
            {profileData.maxWinStreak >= 5 && (
              <div className="achievement-card">
                <div className="achievement-icon">
                  <Zap className="w-6 h-6" />
                </div>
                <div className="achievement-content">
                  <h5>{t('streakWarrior', 'Streak Warrior')}</h5>
                  <p>{profileData.maxWinStreak} {t('maxStreak', 'max streak')}</p>
                  <div className="achievement-desc">
                    {t('streakDesc', 'Achieved impressive win streaks')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Level Progress (if visible) */}
      {profileData.showLevelProgress && (
        <div className="level-section">
          <h4 className="section-title">{t('levelProgress', 'Level Progress')}</h4>
          <div className="level-stats-grid">
            <div className="stat-card level">
              <div className="stat-header">
                <Award className="w-5 h-5" />
                <span>{t('currentLevel', 'Current Level')}</span>
              </div>
              <div className="stat-value large">{profileData.currentLevel}</div>
              <div className="stat-meta">
                {formatNumber(profileData.totalExp)} {t('totalExp', 'Total EXP')}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <Trophy className="w-5 h-5" />
                <span>{t('globalRank', 'Global Rank')}</span>
              </div>
              <div className="stat-value">#{profileData.rank}</div>
              <div className="stat-meta">
                {t('byExperience', 'By Experience')}
              </div>
            </div>

            {profileData.showBadges && (
              <div className="stat-card">
                <div className="stat-header">
                  <Star className="w-5 h-5" />
                  <span>{t('badgesEarned', 'Badges Earned')}</span>
                </div>
                <div className="stat-value">{profileData.badgeCount}</div>
                <div className="stat-meta">
                  {formatNumber(profileData.totalBadgeExp)} {t('badgeExp', 'Badge EXP')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="summary-section">
        <h4 className="section-title">{t('playerSummary', 'Player Summary')}</h4>
        <div className="summary-card">
          <div className="summary-content">
            <p>
              <strong>{profileData.inGameName}</strong> {t('playerSummaryText', 'is a {performance} player with {wins} wins out of {total} matches, achieving a {winRate}% win rate. They are currently ranked #{rank} globally.')
                .replace('{performance}', performance.text.toLowerCase())
                .replace('{wins}', profileData.wins)
                .replace('{total}', profileData.totalGames)
                .replace('{winRate}', profileData.winRate)
                .replace('{rank}', profileData.rank)}
            </p>
            
            {profileData.currentWinStreak > 0 && (
              <p>
                {t('currentStreakText', 'They are currently on a {streak} win streak!')
                  .replace('{streak}', profileData.currentWinStreak)}
              </p>
            )}
            
            {profileData.maxWinStreak >= 5 && (
              <p>
                {t('bestStreakText', 'Their best win streak was {streak} consecutive wins.')
                  .replace('{streak}', profileData.maxWinStreak)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicStatistics;